import { GridColDef } from "@mui/x-data-grid-pro";
import {
  CheckIcon,
  FormControlLabel,
  GKButton,
  GKClientDataGrid,
  GKModal,
  Grid2,
  Switch,
  Typography,
} from "components";
import GKCheckbox from "components/GKCheckbox";
import { useMutation } from "hooks/useQueries";
import { ModelPortfolio, SendOrderSheetForm } from "interfaces/OrderSimulation";
import { Controller, useForm, useFormContext } from "react-hook-form";
import axiosInstance from "utils/axiosInstance";
import { extraColumnsField } from "utils/helpers";

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  sendEmailType?: string;
  matchedPortfolios: ModelPortfolio[];
  modelId: number;
}

const TradeConfirmationModal = (props: Props) => {
  const { open, setOpen, sendEmailType, matchedPortfolios, modelId } = props;

  const modelPortfolioForm = useFormContext();

  const form = useForm<SendOrderSheetForm>({
    defaultValues: {
      portfolioIds: [],
      whatsappPortfolios: [],
      useMarketPrice: false,
    },
  });

  const { mutate, isLoading } = useMutation({
    mutationKey: ["GENERATE_SHEET_AND_SEND"],
    mutationFn: (data) =>
      axiosInstance.post(
        `/model_portfolio/${modelId}/save-send-order-manager/`,
        data,
      ),
    onSuccess: () => setOpen(false),
  });

  const onSubmit = (values: SendOrderSheetForm) => {
    const isRebalanceByCompany =
      modelPortfolioForm.getValues("allocation")?.allocType === "rebalance" &&
      modelPortfolioForm.getValues("allocation")?.allocationBy === "company";

    const data = {
      ...modelPortfolioForm.getValues("allocation"),
      ...modelPortfolioForm.getValues("generateOrderSheet"),
      ...values,
      sendForExecution: sendEmailType === "ORDER",
      useCurrentAllocation:
        modelPortfolioForm.getValues("allocation")?.useCurrentAllocation ===
        "current",
      stockWise: !!isRebalanceByCompany,
      allocationBy: isRebalanceByCompany
        ? "version"
        : modelPortfolioForm.getValues("allocation.allocationBy"),
    };

    mutate(data);
  };

  const isTrade = sendEmailType === "TRADE";

  const columns: (GridColDef & { visibleType?: string[] })[] = [
    {
      ...extraColumnsField(
        `User Name (${matchedPortfolios?.length})`,
        "clientName",
        200,
        1,
      ),
      visibleType: ["TRADE", "ORDER"],
      renderCell: (params) => {
        return (
          <Grid2
            container
            display={"flex"}
            flexDirection={"column"}
            alignItems={"flex-start"}
          >
            <Typography fontWeight={700}>{params?.row?.clientName}</Typography>
            <Typography fontSize={12}>
              {params?.row?.portfolioName} - {params?.row?.portfolioCode}
            </Typography>
          </Grid2>
        );
      },
    },
    {
      ...extraColumnsField(
        "Email Address",
        isTrade ? "orderSheetClientEmail" : "clientEmail",
        300,
        1,
      ),
      visibleType: ["TRADE", "ORDER"],
      sortable: false,
      align: "left",
      valueGetter: (params) =>
        isTrade
          ? params.row.orderSheetClientEmail
          : params.row.clientEmail || "-",
      renderHeader: () => {
        const allEmailsExist = matchedPortfolios?.every((item: any) =>
          isTrade ? !item.orderSheetClientEmail : !item.clientEmail,
        );

        const validPortfolios = matchedPortfolios?.filter(
          (dataItem: any) =>
            (isTrade
              ? dataItem?.orderSheetClientEmail
              : dataItem?.clientEmail) && dataItem.clientPhoneNumber,
        );

        return (
          <Grid2 container gap={1} display={"flex"} alignItems={"center"}>
            <GKCheckbox
              disabled={allEmailsExist}
              indeterminate={allEmailsExist}
              checked={
                validPortfolios?.length === form.watch("portfolioIds").length ||
                false
              }
              onChange={(e) => {
                e.stopPropagation();
                form.setValue(
                  "portfolioIds",
                  e.target.checked
                    ? validPortfolios.map((dataItem) => dataItem?.portfolioId)
                    : [],
                );
              }}
            />
            <Typography fontWeight={600} fontSize={12}>
              Email
            </Typography>
          </Grid2>
        );
      },
      renderCell: (params) => {
        return (
          <Grid2 container gap={1} display={"flex"} alignItems={"center"}>
            <Controller
              name="portfolioIds"
              control={form.control}
              render={({ field: { onChange, value } }) => {
                return (
                  <GKCheckbox
                    sx={{
                      padding: 0,
                    }}
                    disabled={
                      !params.row.clientPhoneNumber ||
                      (isTrade
                        ? !params?.row?.orderSheetClientEmail
                        : !params.row.clientEmail)
                    }
                    indeterminate={
                      !params.row.clientPhoneNumber ||
                      (isTrade
                        ? !params?.row?.orderSheetClientEmail
                        : !params.row.clientEmail)
                    }
                    checked={value.includes(params.row.portfolioId)}
                    onChange={(e) =>
                      onChange(
                        e.target.checked
                          ? [...value, params.row.portfolioId]
                          : value.filter(
                              (id: number) => id !== params.row.portfolioId,
                            ),
                      )
                    }
                  />
                );
              }}
            />
            <Typography>
              {isTrade
                ? params?.row?.orderSheetClientEmail
                : params.row.clientEmail || "-"}
            </Typography>
          </Grid2>
        );
      },
    },
    {
      ...extraColumnsField("Phone No.", "clientPhoneNumber", 170, 1),
      visibleType: ["TRADE", "ORDER"],
      sortable: false,
      valueGetter: (params) => params.row.clientPhoneNumber || "-",
      renderHeader: () => {
        const allEmailsExist = matchedPortfolios?.every((item: any) =>
          isTrade ? !item.orderSheetClientEmail : !item.clientEmail,
        );

        const validPortfolios = matchedPortfolios?.filter(
          (dataItem: any) =>
            (isTrade
              ? dataItem?.orderSheetClientEmail
              : dataItem?.clientEmail) && dataItem.clientPhoneNumber,
        );

        return (
          <Grid2 container gap={1} display={"flex"} alignItems={"center"}>
            <GKCheckbox
              disabled={allEmailsExist}
              indeterminate={allEmailsExist}
              checked={
                validPortfolios?.length ===
                  form.watch("whatsappPortfolios")?.length || false
              }
              onChange={(e) => {
                e.stopPropagation();
                form.setValue(
                  "whatsappPortfolios",
                  e.target.checked
                    ? validPortfolios?.map((dataItem) => dataItem?.portfolioId)
                    : [],
                );
              }}
            />
            <Typography fontWeight={600} fontSize={12}>
              Phone
            </Typography>
          </Grid2>
        );
      },
      renderCell: (params) => {
        return (
          <Grid2 container gap={1} display={"flex"} alignItems={"center"}>
            <Controller
              name="whatsappPortfolios"
              control={form.control}
              render={({ field: { onChange, value } }) => (
                <GKCheckbox
                  sx={{ padding: 0 }}
                  disabled={
                    !params.row.clientPhoneNumber ||
                    (isTrade
                      ? !params?.row?.orderSheetClientEmail
                      : !params.row.clientEmail)
                  }
                  indeterminate={
                    !params.row.clientPhoneNumber ||
                    (isTrade
                      ? !params?.row?.orderSheetClientEmail
                      : !params.row.clientEmail)
                  }
                  checked={value.includes(params.row.portfolioId)}
                  onChange={(e) =>
                    onChange(
                      e.target.checked
                        ? [...value, params.row.portfolioId]
                        : value.filter(
                            (id: number) => id !== params.row.portfolioId,
                          ),
                    )
                  }
                />
              )}
            />
            <Typography>{params.row.clientPhoneNumber || "-"}</Typography>
          </Grid2>
        );
      },
    },
  ];

  return (
    <GKModal
      open={open}
      setOpen={setOpen}
      onClose={() => setOpen(false)}
      modalTitle={
        sendEmailType === "TRADE" ? "Send Order Sheet" : "Send for Execution"
      }
      footer={
        <Grid2 container justifyContent={"flex-end"}>
          <GKButton
            variant="contained"
            type="submit"
            form="pre-trade-confirmation-form"
            startIcon={<CheckIcon />}
            loading={isLoading}
          >
            {sendEmailType === "TRADE"
              ? "Send Order Sheet"
              : "Send for Execution"}
          </GKButton>
        </Grid2>
      }
    >
      <form
        id="pre-trade-confirmation-form"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <GKClientDataGrid
          headerComponent={
            <Controller
              name="useMarketPrice"
              control={form.control}
              render={({ field }) => {
                return (
                  <FormControlLabel
                    {...field}
                    control={
                      <Switch
                        checked={field.value}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                        }}
                      />
                    }
                    label="Use Market Price"
                  />
                );
              }}
            />
          }
          name="preTradeConfirmationModal"
          columns={columns?.filter((d) =>
            d?.visibleType?.includes(sendEmailType),
          )}
          rows={
            matchedPortfolios.map((dataItem, index) => ({
              ...dataItem,
              id: index,
            })) || []
          }
          maxHeight={"calc(100vh - 355px)"}
        />
      </form>
    </GKModal>
  );
};

export default TradeConfirmationModal;
